import { Command as CommandPrimitive } from 'cmdk'
import { LucideX } from 'lucide-react'
import { useCallback } from 'react'
import { useMap } from 'react-map-gl'
import { aIsMapEasing, aMarkLocation, useSetAtom } from '~/atoms'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Input,
} from '~/components/ui'
import { useGeocodeSearch } from '~/hooks/map'
import { cn } from '~/lib'
import { getMapCardSizes } from '~/lib.client'

export default function MapSearch({ className }: { className?: string }) {
  const mapRef = useMap()
  const setMarkLocation = useSetAtom(aMarkLocation)
  const setIsMapEasing = useSetAtom(aIsMapEasing)
  const { setSearch, search, predictions, getPlaceData } = useGeocodeSearch()

  const onSelectedPrediction = useCallback(
    async (prediction: google.maps.places.AutocompletePrediction) => {
      const placeData = await getPlaceData(prediction)
      if (!placeData) {
        // error
      } else {
        const { lng, lat, zoom } = placeData
        setMarkLocation(true)
        setIsMapEasing(true)
        mapRef.current!.easeTo({
          zoom: zoom > 16 ? 16 : zoom,
          center: [lng, lat],
          padding: {
            left: getMapCardSizes().list,
            top: 0,
            right: 0,
            bottom: 0,
          },
          duration: 500,
        })
        setTimeout(() => {
          setIsMapEasing(false)
        }, 510)
      }
      setSearch('')
    },
    []
  )

  return (
    <Command
      className={cn(
        'relative -m-1 w-40 bg-transparent p-1 lg:w-40 xl:w-64',
        className
      )}>
      <CommandPrimitive.Input
        asChild
        value={search}
        onValueChange={(value) => {
          setSearch(value)
        }}>
        <Input
          className="w-full border-0 pr-8 text-base shadow-md"
          placeholder="Search Address..."
        />
      </CommandPrimitive.Input>
      {search.length > 0 && (
        <button
          onClick={() => setSearch('')}
          className="absolute right-0 top-1 h-10 px-3 text-black">
          <LucideX className="h-4 w-4" />
          <span className="sr-only">Clear address search</span>
        </button>
      )}

      <CommandList
        className={cn(
          'mt-1 rounded-md bg-background',
          search.trim().length === 0 && 'hidden'
        )}>
        <CommandEmpty className="px-3 py-2 text-sm">
          No addresses found
        </CommandEmpty>
        {predictions.length > 0 && (
          <CommandGroup>
            {predictions.map((prediction) => (
              <CommandItem
                key={prediction.place_id}
                className="whitespace-pre-line"
                onSelect={() => onSelectedPrediction(prediction)}>
                {`${prediction.structured_formatting.main_text}\n${prediction.structured_formatting.secondary_text}`}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  )
}
